import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { listMySignals } from "@/lib/user.functions";

export const Route = createFileRoute("/_authenticated/signals")({
  component: SignalsPage,
});

type Filter = "All" | "BUY" | "SELL" | "WAIT";
const FILTERS: Filter[] = ["All", "BUY", "SELL", "WAIT"];

const LEGEND_NAMES = [
  "George Soros",
  "Paul Tudor Jones",
  "Richard Dennis",
  "Stanley Druckenmiller",
  "Bruce Kovner",
  "Ed Seykota",
] as const;

interface PlaybookPayload {
  playbookName: string;
  legendArchitect: string;
  similarityPct: number;
  bias: "BUY" | "SELL" | "WAIT";
}

function extractPlaybook(payload: unknown): PlaybookPayload | null {
  if (!payload || typeof payload !== "object") return null;
  const p = (payload as { playbookMatch?: unknown }).playbookMatch;
  if (!p || typeof p !== "object") return null;
  return p as PlaybookPayload;
}

function fmtTime(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toISOString().slice(0, 10),
    time: d.toISOString().slice(11, 16) + " UTC",
  };
}

function SignalsPage() {
  const fetchSignals = useServerFn(listMySignals);
  const { data, isLoading } = useQuery({
    queryKey: ["my-signals"],
    queryFn: () => fetchSignals(),
    refetchInterval: 30_000,
  });
  const signals = data?.signals ?? [];
  const [filter, setFilter] = useState<Filter>("All");
  const [legendFilter, setLegendFilter] = useState<string>("All");
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let out = signals;
    if (filter !== "All") out = out.filter((s) => s.action === filter);
    if (legendFilter !== "All") {
      out = out.filter((s) => extractPlaybook(s.payload)?.legendArchitect === legendFilter);
    }
    return out;
  }, [signals, filter, legendFilter]);

  const stats = useMemo(() => {
    const total = signals.length;
    const buys = signals.filter((s) => s.action === "BUY").length;
    const sells = signals.filter((s) => s.action === "SELL").length;
    const avgScore = total ? signals.reduce((a, s) => a + Number(s.score), 0) / total : 0;
    return {
      total,
      buys,
      sells,
      avgScore: avgScore.toFixed(1),
    };
  }, [signals]);

  return (
    <main className="flex-1 p-4 lg:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Signal History</h1>
          <p className="text-sm text-muted-foreground">
            Every BUY/SELL flip is saved to your account.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard">Back to dashboard</Link>
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Total Signals" value={stats.total.toString()} />
        <StatCard label="Buys" value={stats.buys.toString()} accent="var(--buy)" />
        <StatCard label="Sells" value={stats.sells.toString()} accent="var(--sell)" />
        <StatCard label="Avg Score" value={stats.avgScore} accent="var(--primary)" />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            className={cn(
              "h-8 text-xs",
              filter === f
                ? "bg-primary text-primary-foreground"
                : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground",
            )}
          >
            {f}
          </Button>
        ))}
        <select
          value={legendFilter}
          onChange={(e) => setLegendFilter(e.target.value)}
          className="h-8 rounded-md border border-border/60 bg-card/40 px-2 text-xs text-muted-foreground"
        >
          <option value="All">All legends</option>
          {LEGEND_NAMES.map((n) => (
            <option key={n} value={n}>
              Only {n}
            </option>
          ))}
        </select>
        <span className="ml-auto self-center text-xs text-muted-foreground">
          Showing {filtered.length} of {signals.length}
        </span>
      </div>

      <Card className="overflow-hidden border-border/60 bg-card/60">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border/60 hover:bg-transparent">
                {["Time", "Pair", "Action", "Entry", "SL", "TP", "Score", "Playbook"].map((h) => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-widest">
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    Loading signals…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    No signals yet — open the dashboard to start generating them.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => {
                const t = fmtTime(s.created_at);
                const dirColor =
                  s.action === "BUY"
                    ? "var(--buy)"
                    : s.action === "SELL"
                      ? "var(--sell)"
                      : "var(--warn)";
                const pb = extractPlaybook(s.payload);
                const isOpen = expanded === s.id;
                return (
                  <Fragment key={s.id}>

                    <TableRow key={s.id} className="border-border/40 font-mono text-sm">
                      <TableCell className="text-muted-foreground">
                        <div>{t.date}</div>
                        <div className="text-[10px]">{t.time}</div>
                      </TableCell>
                      <TableCell className="font-semibold">{`${s.pair.slice(0, 3)}/${s.pair.slice(3)}`}</TableCell>
                      <TableCell>
                        <span
                          className="rounded-md px-2 py-0.5 text-xs font-bold"
                          style={{
                            backgroundColor: `color-mix(in oklab, ${dirColor} 18%, transparent)`,
                            color: dirColor,
                          }}
                        >
                          {s.action}
                        </span>
                      </TableCell>
                      <TableCell>{s.entry ?? "—"}</TableCell>
                      <TableCell className="text-sell/80">{s.stop_loss ?? "—"}</TableCell>
                      <TableCell className="text-buy/80">{s.take_profit ?? "—"}</TableCell>
                      <TableCell>
                        <span
                          style={{
                            color:
                              Number(s.score) >= 80
                                ? "var(--buy)"
                                : Number(s.score) >= 65
                                  ? "var(--warn)"
                                  : "var(--sell)",
                          }}
                        >
                          {Number(s.score).toFixed(0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {pb ? (
                          <button
                            onClick={() => setExpanded(isOpen ? null : s.id)}
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-warn/10"
                            style={{ borderColor: "#FFD700", color: "#FFD700" }}
                          >
                            🏛️ {pb.legendArchitect.split(" ").slice(-1)[0]} · {pb.similarityPct}%
                          </button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && pb && (
                      <TableRow className="border-border/40">
                        <TableCell colSpan={8} className="bg-[#0a0e1a] p-4">
                          <div
                            className="rounded-md p-3"
                            style={{ borderLeft: "3px solid #FFD700" }}
                          >
                            <div className="text-xs font-bold" style={{ color: "#FFD700" }}>
                              {pb.playbookName}
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              Legend: <span className="text-foreground">{pb.legendArchitect}</span>
                              {" · "}Bias: <span className="text-foreground">{pb.bias}</span>
                              {" · "}Match: <span className="text-foreground">{pb.similarityPct}%</span>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </main>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Card className="border-border/60 bg-card/60 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-2xl font-bold" style={{ color: accent ?? "inherit" }}>
        {value}
      </div>
    </Card>
  );
}
